import React, { useMemo, useState } from "react";
import { ExpandMore } from "@mui/icons-material";

import {
  DateFormat,
  formatCurrency,
  formatDate,
  formatPercent,
  formatSignedCurrency,
} from "../../../../../utils";
import { LiabilityInterestRateHistory } from "../../../types";
import { ChangeIndicator } from "./ChangeIndicator";
import { RateRowMenu } from "./RateRowMenu";
import { buildRateRows, buildRateSummary, buildSubtitle } from "./helpers";
import {
  Cell,
  CardBottom,
  CardList,
  CardMeta,
  CardPayment,
  CardPaymentDelta,
  CardPaymentLabel,
  CardPaymentValue,
  CardRate,
  CardRateWrap,
  CardTop,
  ChangeCell,
  CollapseChevron,
  CurrentBadge,
  DateValue,
  DesktopHeader,
  Footer,
  FooterMeta,
  FooterNote,
  HeaderLeft,
  HeaderStat,
  HeaderStatLabel,
  HeaderStatValue,
  HeaderStats,
  HeaderSubtitle,
  HeaderTitle,
  MenuCell,
  MobileHeader,
  MobileHeaderLeft,
  PaymentDelta,
  PaymentValue,
  PeriodCard,
  PeriodsPill,
  RateCard,
  RateValue,
  Table,
  TableHead,
  TableHeadCell,
  TableRow,
} from "./styles";
import { InterestRateHistoryProps } from "./types";

const EMPTY_PAYMENT = "—";
const FOOTER_META = "All shown";

export const InterestRateHistory: React.FC<InterestRateHistoryProps> =
  React.memo(({ liability, onEditRate, onDuplicateRate, onDeleteRate }) => {
    const [isExpanded, setIsExpanded] = useState(true);

    const summary = useMemo(() => buildRateSummary(liability), [liability]);
    const rows = useMemo(() => buildRateRows(liability), [liability]);
    const rateById = useMemo(() => {
      const map = new Map<number, LiabilityInterestRateHistory>();
      (liability.interestRateHistory ?? []).forEach((entry) => {
        map.set(entry.id, entry);
      });
      return map;
    }, [liability]);

    if (!summary || rows.length === 0) {
      return null;
    }

    const subtitle = buildSubtitle(summary);
    const currentRateLabel = formatPercent(summary.currentRate, 3);
    const rangeLabel = `${formatPercent(summary.minRate, 3)} – ${formatPercent(
      summary.maxRate,
      3,
    )}`;
    const mobileSubtitle = `Range ${formatPercent(
      summary.minRate,
      3,
    )}–${formatPercent(summary.maxRate, 3)} · now ${currentRateLabel}`;
    const periodsLabel = `${summary.periodCount} periods`;
    const footerNote = `${summary.periodCount} rate periods since origination`;
    const nextResetLabel = summary.nextResetDate
      ? formatDate(summary.nextResetDate, DateFormat.DMY_SPACE)
      : null;

    return (
      <RateCard data-testid="interest-rate-history">
        <DesktopHeader>
          <HeaderLeft>
            <HeaderTitle>Interest rate history</HeaderTitle>
            <HeaderSubtitle>{subtitle}</HeaderSubtitle>
          </HeaderLeft>
          <HeaderStats>
            <HeaderStat>
              <HeaderStatLabel>Current rate</HeaderStatLabel>
              <HeaderStatValue $accent>{currentRateLabel}</HeaderStatValue>
            </HeaderStat>
            {nextResetLabel && (
              <HeaderStat>
                <HeaderStatLabel>Next reset</HeaderStatLabel>
                <HeaderStatValue>{nextResetLabel}</HeaderStatValue>
              </HeaderStat>
            )}
            <HeaderStat>
              <HeaderStatLabel>Range so far</HeaderStatLabel>
              <HeaderStatValue>{rangeLabel}</HeaderStatValue>
            </HeaderStat>
          </HeaderStats>
        </DesktopHeader>

        <MobileHeader>
          <MobileHeaderLeft
            type="button"
            onClick={() => setIsExpanded((prev) => !prev)}
            aria-expanded={isExpanded}
          >
            <CollapseChevron $expanded={isExpanded} aria-hidden>
              <ExpandMore fontSize="small" />
            </CollapseChevron>
            <HeaderLeft>
              <HeaderTitle>Interest rate history</HeaderTitle>
              <HeaderSubtitle>{mobileSubtitle}</HeaderSubtitle>
            </HeaderLeft>
          </MobileHeaderLeft>
          <PeriodsPill>{periodsLabel}</PeriodsPill>
        </MobileHeader>

        <Table>
          <TableHead>
            <TableHeadCell>Took effect</TableHeadCell>
            <TableHeadCell>Rate</TableHeadCell>
            <TableHeadCell>Monthly payment</TableHeadCell>
            <TableHeadCell $align="right">Change</TableHeadCell>
            <TableHeadCell aria-hidden />
          </TableHead>

          {rows.map((row) => {
            const paymentLabel =
              row.paymentAmount === null
                ? EMPTY_PAYMENT
                : formatCurrency(row.paymentAmount);
            const rateEntry = rateById.get(row.id);

            return (
              <TableRow key={row.id}>
                <Cell>
                  <DateValue>
                    {formatDate(row.effectiveDate, DateFormat.DMY_SPACE)}
                  </DateValue>
                </Cell>
                <Cell>
                  <RateValue>{formatPercent(row.rate, 3)}</RateValue>
                </Cell>
                <Cell>
                  <PaymentValue>{paymentLabel}</PaymentValue>
                  {row.paymentDelta !== null && (
                    <PaymentDelta $delta={row.paymentDelta}>
                      {formatSignedCurrency(row.paymentDelta)} vs prior
                    </PaymentDelta>
                  )}
                </Cell>
                <ChangeCell>
                  <ChangeIndicator
                    isOrigination={row.isOrigination}
                    direction={row.rateDirection}
                    deltaPp={row.rateDeltaPp}
                  />
                </ChangeCell>
                <MenuCell>
                  {rateEntry && (
                    <RateRowMenu
                      rate={rateEntry}
                      onEditRate={onEditRate}
                      onDuplicateRate={onDuplicateRate}
                      onDeleteRate={onDeleteRate}
                    />
                  )}
                </MenuCell>
              </TableRow>
            );
          })}
        </Table>

        {isExpanded && (
          <CardList>
            {rows.map((row) => {
              const mobilePaymentLabel =
                row.paymentAmount === null
                  ? EMPTY_PAYMENT
                  : `${formatCurrency(row.paymentAmount)}/mo`;
              const rateEntry = rateById.get(row.id);

              return (
                <PeriodCard key={row.id}>
                  <CardTop>
                    <CardRateWrap>
                      <CardRate>{formatPercent(row.rate, 3)}</CardRate>
                      {row.isCurrent && <CurrentBadge>Current</CurrentBadge>}
                    </CardRateWrap>
                    {rateEntry && (
                      <RateRowMenu
                        rate={rateEntry}
                        onEditRate={onEditRate}
                        onDuplicateRate={onDuplicateRate}
                        onDeleteRate={onDeleteRate}
                      />
                    )}
                  </CardTop>

                  <CardMeta>
                    {row.isScheduled
                      ? `Takes effect ${formatDate(row.effectiveDate, DateFormat.DMY_SPACE)}`
                      : `Took effect ${formatDate(row.effectiveDate, DateFormat.DMY_SPACE)} · held ${row.heldLabel}`}
                  </CardMeta>

                  <CardBottom>
                    <CardPayment>
                      <CardPaymentLabel>Payment</CardPaymentLabel>
                      <CardPaymentValue>{mobilePaymentLabel}</CardPaymentValue>
                      {row.paymentDelta !== null && (
                        <CardPaymentDelta $delta={row.paymentDelta}>
                          {formatSignedCurrency(row.paymentDelta)}
                        </CardPaymentDelta>
                      )}
                    </CardPayment>
                    <ChangeIndicator
                      isOrigination={row.isOrigination}
                      direction={row.rateDirection}
                      deltaPp={row.rateDeltaPp}
                    />
                  </CardBottom>
                </PeriodCard>
              );
            })}
          </CardList>
        )}

        <Footer>
          <FooterNote>{footerNote}</FooterNote>
          <FooterMeta>{FOOTER_META}</FooterMeta>
        </Footer>
      </RateCard>
    );
  });

InterestRateHistory.displayName = "InterestRateHistory";
